<template>
  <LoadingIndicator v-if="loading" message="Loading template..." />
  <div v-else-if="loadError" class="rounded-md bg-red-tint px-4 py-3 text-sm text-gray-900">{{ loadError }}</div>
  <div v-else class="space-y-6">
    <div v-if="!isNew" class="flex items-center gap-3 text-sm">
      <router-link :to="{ name: 'AdminHermesTemplates' }" :class="TEXT_BUTTON">← All templates</router-link>
      <span class="text-xs text-gray-500">#{{ saved.id }} · version {{ saved.version }}<span v-if="saved.legacy_sat"> · replaces SAT {{ saved.legacy_sat }} ({{ saved.legacy_sat_name }})</span></span>
      <router-link :to="{ name: 'AdminHermesRuns', query: { template: saved.id } }" :class="[TEXT_BUTTON, 'ml-auto']">View runs</router-link>
      <router-link :to="{ name: 'AdminHermesRuns', query: { start: saved.id } }" :class="SECONDARY_BUTTON">Start run…</router-link>
    </div>

    <div class="grid grid-cols-12 gap-6 items-start">
      <div class="col-span-8 space-y-6">
        <HermesCard title="Identity" collapsible>
          <div class="grid grid-cols-12 gap-4">
            <label class="col-span-8 text-sm"><span class="block text-xs text-gray-500 mb-2">Name</span><input v-model="form.name" :class="INPUT"></label>
            <label class="col-span-4 text-sm flex items-center gap-2 pt-6"><input v-model="form.is_active" type="checkbox" class="rounded border-gray-300"> Active; runs and schedules can use it</label>
            <label class="col-span-12 text-sm"><span class="block text-xs text-gray-500 mb-2">Description</span><textarea v-model="form.description" rows="2" :class="INPUT" /></label>
          </div>
        </HermesCard>

        <HermesCard title="Who it's about" collapsible>
          <div class="grid grid-cols-12 gap-4">
            <label class="col-span-4 text-sm">
              <span class="block text-xs text-gray-500 mb-2">Unit column</span>
              <input v-model="form.unit" :class="[INPUT, 'font-mono']" placeholder="BENE_MBI_ID">
              <span class="block text-xs text-gray-500 mt-2">The key column one result is about.</span>
            </label>
            <div class="col-span-8 space-y-2">
              <label :class="['flex gap-3 rounded-md border p-3 cursor-pointer', form.population ? 'border-primary-500 bg-primary-50' : 'border-gray-200']">
                <input type="radio" :checked="!!form.population" class="mt-1" @change="form.population = { kind: 'data_attribute', ref: null }">
                <span class="flex-1 text-sm">
                  <span class="block font-medium text-gray-900">A DataAttribute returns the units</span>
                  <select v-if="form.population" v-model.number="form.population.ref" :class="[INPUT, 'mt-2']">
                    <option :value="null">Choose a DataAttribute</option>
                    <option v-for="d in catalog.data_attributes" :key="d.id" :value="d.id">{{ d.name }} (#{{ d.id }})</option>
                  </select>
                </span>
              </label>
              <label :class="['flex gap-3 rounded-md border p-3 cursor-pointer', !form.population ? 'border-primary-500 bg-primary-50' : 'border-gray-200']">
                <input type="radio" :checked="!form.population" class="mt-1" @change="form.population = null">
                <span class="text-sm"><span class="block font-medium text-gray-900">None; the caller passes one unit</span>
                  <span class="block text-xs text-gray-600">For templates an event or a person starts for a single patient.</span></span>
              </label>
            </div>
          </div>
        </HermesCard>

        <HermesCard title="Inputs" :meta="`${form.inputs.length} sources, in the order the model reads them`" collapsible>
          <div class="space-y-4">
            <div v-for="(item, i) in form.inputs" :key="i" class="rounded-md border border-gray-200 p-4">
              <div class="flex items-center gap-3 mb-3">
                <span class="text-xs text-gray-500 tabular-nums">{{ i + 1 }}</span>
                <span class="rounded bg-neutral-100 px-2 text-xs text-gray-900">{{ words(item.kind) }}</span>
                <span class="font-mono text-sm">{{ item.label }}</span>
                <div class="ml-auto flex gap-3">
                  <button type="button" :class="TEXT_BUTTON" :disabled="i === 0" @click="move(i, -1)">Move up</button>
                  <button type="button" :class="TEXT_BUTTON" :disabled="i === form.inputs.length - 1" @click="move(i, 1)">Move down</button>
                  <button type="button" :class="TEXT_BUTTON" @click="form.inputs.splice(i, 1)">Remove</button>
                </div>
              </div>
              <div class="grid grid-cols-12 gap-4">
                <label class="col-span-4 text-sm"><span class="block text-xs text-gray-500 mb-2">Label</span><input v-model="item.label" :class="[INPUT, 'font-mono']"></label>
                <label class="col-span-8 text-sm"><span class="block text-xs text-gray-500 mb-2">Description</span><input v-model="item.description" :class="INPUT"></label>
                <template v-if="item.kind === 'data_attribute'">
                  <label class="col-span-6 text-sm">
                    <span class="block text-xs text-gray-500 mb-2">DataAttribute</span>
                    <select v-model.number="item.ref" :class="INPUT">
                      <option :value="null">Choose a DataAttribute</option>
                      <option v-for="d in catalog.data_attributes" :key="d.id" :value="d.id">{{ d.name }} (#{{ d.id }})</option>
                    </select>
                    <span class="block text-xs text-gray-500 mt-2">Narrowed to each unit on {{ form.unit || 'the unit column' }}.</span>
                  </label>
                  <div class="col-span-6 text-sm">
                    <span class="block text-xs text-gray-500 mb-2">Filters (fixed value, or {param} to bind a run parameter)</span>
                    <div v-for="(f, j) in filterRows(item)" :key="j" class="flex gap-2 mb-2">
                      <input :value="f.name" :class="[INPUT, 'font-mono']" placeholder="name" @input="setFilter(item, j, 'name', $event.target.value)">
                      <input :value="f.value" :class="[INPUT, 'font-mono', String(f.value).startsWith('{') ? 'border-dashed border-primary-500' : '']" placeholder="value or {param}" @input="setFilter(item, j, 'value', $event.target.value)">
                      <button type="button" :class="TEXT_BUTTON" @click="removeFilter(item, j)">Remove</button>
                    </div>
                    <button type="button" :class="TEXT_BUTTON" @click="addFilter(item)">Add filter</button>
                  </div>
                  <label class="col-span-6 text-sm">
                    <span class="block text-xs text-gray-500 mb-2">Render as</span>
                    <select :value="item.render || ''" :class="INPUT" @change="setRender(item, $event.target.value)">
                      <option value="">Rows (column=value lines)</option>
                      <option v-for="r in catalog.renderers || []" :key="r" :value="r">{{ words(r) }}</option>
                    </select>
                    <span class="block text-xs text-gray-500 mt-2">A named renderer expects the DataAttribute's columns in the shape it was written for.</span>
                  </label>
                </template>
                <template v-else-if="item.kind === 'patient_graph'">
                  <div class="col-span-12 text-sm">
                    <label class="inline-flex items-center gap-2 mb-3"><input v-model="item.required" type="checkbox" class="rounded border-gray-300"> Required; a unit with no graph fails instead of running without it</label>
                    <div class="grid grid-cols-12 gap-2">
                      <label v-for="n in catalog.graph_nodes" :key="n.key" class="col-span-4 inline-flex items-center gap-2 text-xs">
                        <input type="checkbox" :checked="(item.nodes || catalog.default_graph_nodes).includes(n.key)" @change="toggleNode(item, n.key)"> {{ n.label }}
                      </label>
                    </div>
                  </div>
                </template>
                <label v-else class="col-span-6 text-sm">
                  <span class="block text-xs text-gray-500 mb-2">Source</span>
                  <select v-model="item.ref" :class="INPUT">
                    <option v-for="s in catalog.sources" :key="s.name" :value="s.name">{{ s.name }}</option>
                  </select>
                </label>
              </div>
            </div>
            <div class="flex gap-3">
              <span class="text-sm text-gray-600">Add input:</span>
              <button v-for="k in ['data_attribute', 'patient_graph', 'source']" :key="k" type="button" :class="TEXT_BUTTON" @click="addInput(k)">{{ words(k) }}</button>
            </div>
          </div>
        </HermesCard>

        <HermesCard title="Prompt" collapsible>
          <p class="text-xs text-gray-500 mb-2">Write {name} where a run parameter goes. Wrap a clause in {param_start:name} … {param_end:name} to drop it when the run omits the value.</p>
          <textarea v-model="form.prompt" rows="18" :class="[INPUT, 'font-mono text-xs leading-5']" spellcheck="false" />
        </HermesCard>

        <HermesCard title="Result schema" :meta="`${fields.length} fields`" collapsible>
          <template #actions>
            <div class="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs">
              <button v-for="v in ['fields', 'json']" :key="v" type="button" :class="['px-3 py-1', schemaView === v ? 'bg-neutral-100 text-gray-900' : 'bg-white text-gray-600']" @click="schemaView = v">{{ v === 'json' ? 'Raw JSON' : 'Fields' }}</button>
            </div>
          </template>
          <template v-if="schemaView === 'json'">
            <textarea v-model="schemaText" rows="16" :class="[INPUT, 'font-mono text-xs']" spellcheck="false" />
            <p v-if="schemaError" class="mt-2 rounded bg-red-tint px-2 py-1 text-xs text-gray-900">{{ schemaError }}</p>
            <button type="button" :class="[TEXT_BUTTON, 'mt-2']" @click="schemaText = ''">Clear schema</button>
          </template>
          <template v-else>
            <p v-if="schemaError" class="text-sm text-gray-600">Fix the JSON to see fields.</p>
            <p v-else-if="!fields.length" class="text-sm text-gray-600">No schema: each result is free text.</p>
            <table v-else class="w-full table-fixed text-sm">
              <colgroup><col style="width:24%"><col style="width:14%"><col style="width:10%"><col style="width:24%"><col style="width:28%"></colgroup>
              <thead><tr><th :class="TH">Field</th><th :class="TH">Type</th><th :class="TH">Required</th><th :class="TH">Allowed</th><th :class="TH">Description</th></tr></thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="f in flatFields" :key="f.path">
                  <td class="px-3 py-2 font-mono text-xs" :style="{ paddingLeft: `${12 + f.depth * 16}px` }">{{ f.name }}</td>
                  <td class="px-3 py-2 text-xs">{{ f.type }}</td>
                  <td class="px-3 py-2 text-xs">{{ f.required ? 'Required' : 'Optional' }}</td>
                  <td class="px-3 py-2 text-xs"><span v-for="e in f.enum" :key="e" class="inline-block rounded bg-gray-100 px-2 mr-1 mb-1">{{ e }}</span>{{ f.range }}</td>
                  <td class="px-3 py-2 text-xs text-gray-600">{{ f.description }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </HermesCard>

        <HermesCard title="Effects" collapsible>
          <p class="text-sm text-gray-700 mb-3">Writes this template may make. Each executor still needs a grant for each kind before a run starts.</p>
          <div v-for="area in areas" :key="area" class="mb-3">
            <h3 class="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">{{ area }}</h3>
            <div class="grid grid-cols-12 gap-2">
              <label v-for="k in kinds.filter((x) => x.area === area)" :key="k.kind" class="col-span-6 flex items-start gap-2 text-sm">
                <input v-model="form.effects" type="checkbox" :value="k.kind" class="mt-1 rounded border-gray-300">
                <span><span class="text-gray-900">{{ k.label }}</span> <span class="font-mono text-xs text-gray-500">{{ k.kind }}</span>
                  <span class="block text-xs text-gray-600">{{ k.description }}</span></span>
              </label>
            </div>
          </div>
        </HermesCard>
      </div>

      <div class="col-span-4 space-y-6 sticky top-4">
        <HermesCard title="Run parameters" meta="Derived; read-only">
          <p v-if="!params.length" class="text-sm text-gray-600">No run parameters. This template runs the same way every time.</p>
          <ul v-else class="space-y-3">
            <li v-for="p in params" :key="p.name" class="text-sm">
              <span class="font-mono text-gray-900">{{ p.name }}</span>
              <span :class="['ml-2 rounded px-2 text-xs', p.required ? 'bg-primary-100 text-gray-900' : 'bg-gray-100 text-gray-700']">{{ p.required ? 'Required' : 'Optional' }}</span>
              <span v-if="!savedParamNames.includes(p.name) && !isNew" class="ml-2 rounded bg-neutral-100 px-2 text-xs text-gray-900">New in draft</span>
              <div class="text-xs text-gray-500 mt-1">Used in {{ p.where.join(', ') }}</div>
            </li>
          </ul>
          <p class="text-xs text-gray-500 mt-4">Every run also accepts <span class="font-mono">sample</span> to score a fixed draw of units{{ form.population ? '' : '; this template needs a unit at start instead' }}.</p>
          <p v-if="derivedError" class="mt-3 rounded bg-neutral-50 px-2 py-1 text-xs text-gray-900">{{ derivedError }}</p>
        </HermesCard>

        <HermesCard v-if="!isNew" title="Versions" collapsible>
          <ul class="space-y-3">
            <li v-for="(v, i) in versions" :key="v.version" class="text-sm">
              <span class="font-medium">Version {{ v.version }}</span>
              <span v-if="i === 0" class="ml-2 rounded bg-secondary-200 px-2 text-xs text-gray-900">Current</span>
              <div class="text-xs text-gray-500"><span :title="absolute(v.at)">{{ relative(v.at) }}</span><span v-if="v.by"> · <span class="sensitive">{{ v.by }}</span></span></div>
              <button v-if="versions[i + 1]" type="button" :class="[TEXT_BUTTON, 'text-xs']" @click="diff = { a: versions[i + 1], b: v }">Diff vs {{ versions[i + 1].version }}</button>
            </li>
          </ul>
        </HermesCard>
      </div>
    </div>

    <div class="sticky bottom-0 z-10 -mx-8 border-t border-gray-200 bg-white px-8 py-3 flex items-center gap-4">
      <span v-if="changedSections.length" class="text-xs text-gray-600">Changed: {{ changedSections.join(', ') }}</span>
      <span v-if="savedNote" class="text-xs text-gray-600">{{ savedNote }}</span>
      <div v-if="saveError" role="alert" class="rounded-md bg-red-tint px-3 py-2 text-sm text-gray-900"><strong>Not saved.</strong> {{ saveError }}</div>
      <div class="ml-auto flex gap-3">
        <button v-if="!isNew" type="button" :class="TEXT_BUTTON" :disabled="!changedSections.length || saving" @click="reset">Discard</button>
        <button type="button" :class="PRIMARY_BUTTON" :disabled="saving || (!isNew && !changedSections.length)" @click="save">
          {{ saving ? 'Saving…' : (isNew ? 'Create template' : 'Save new version') }}
        </button>
      </div>
    </div>

    <div v-if="diff" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true">
      <div class="w-[900px] max-h-[80vh] flex flex-col rounded-lg bg-white border border-gray-200">
        <div class="bg-primary-100 px-4 py-3 rounded-t-lg flex items-center text-sm font-semibold text-gray-900">
          Version {{ diff.a.version }} → {{ diff.b.version }}
          <button type="button" class="ml-auto text-sm font-normal text-gray-700" @click="diff = null">Close</button>
        </div>
        <div class="overflow-y-auto p-4 space-y-4">
          <p v-if="!diffSections.length" class="text-sm text-gray-600">No change to the definition between these versions.</p>
          <div v-for="s in diffSections" :key="s.key">
            <h3 class="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">{{ s.key }}</h3>
            <pre class="text-xs whitespace-pre-wrap break-words"><span v-for="(l, i) in s.lines" :key="i" :class="['block px-2', l.op === '-' ? 'bg-gray-100 line-through text-gray-600' : (l.op === '+' ? 'bg-secondary-100 text-gray-900' : 'text-gray-700')]">{{ l.op }} {{ l.text }}</span></pre>
          </div>
        </div>
        <div class="px-4 py-3 border-t border-gray-200 flex justify-end">
          <button type="button" :class="SECONDARY_BUTTON" @click="restore(diff.a)">Restore version {{ diff.a.version }} as a draft</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import HermesCard from './HermesCard.vue';
import { API, INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH, absolute, errorOf, relative, words } from './hermes';

const DEFINITION = ['unit', 'population', 'inputs', 'prompt', 'result_schema', 'effects'];
const BLANK = { name: '', description: '', is_active: true, unit: 'BENE_MBI_ID', population: null, inputs: [], prompt: '', result_schema: null, effects: [] };

function lineDiff(a, b) {
  const x = String(a ?? '').split('\n');
  const y = String(b ?? '').split('\n');
  const dp = Array.from({ length: x.length + 1 }, () => new Array(y.length + 1).fill(0));
  for (let i = x.length - 1; i >= 0; i -= 1) for (let j = y.length - 1; j >= 0; j -= 1) dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out = [];
  let i = 0; let j = 0;
  while (i < x.length && j < y.length) {
    if (x[i] === y[j]) { out.push({ op: ' ', text: x[i] }); i += 1; j += 1; } else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ op: '-', text: x[i] }); i += 1; } else { out.push({ op: '+', text: y[j] }); j += 1; }
  }
  while (i < x.length) { out.push({ op: '-', text: x[i] }); i += 1; }
  while (j < y.length) { out.push({ op: '+', text: y[j] }); j += 1; }
  return out;
}

export default {
  name: 'HermesTemplateEditor',
  components: { LoadingIndicator, HermesCard },
  emits: ['crumb'],
  data() {
    return {
      loading: true, loadError: '', saving: false, saveError: '', savedNote: '', saved: {}, form: JSON.parse(JSON.stringify(BLANK)),
      schemaText: '', schemaView: 'fields', catalog: { data_attributes: [], sources: [], graph_nodes: [], default_graph_nodes: [] },
      kinds: [], areas: [], versions: [], params: [], derivedError: '', derivedTimer: null, diff: null,
      INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    isNew() {
      return this.$route.params.templateId === 'new';
    },
    savedParamNames() {
      return (this.saved.params || []).map((p) => p.name);
    },
    parsedSchema() {
      if (!this.schemaText.trim()) return { value: null, error: '' };
      try {
        return { value: JSON.parse(this.schemaText), error: '' };
      } catch (e) {
        return { value: undefined, error: `The schema is not valid JSON: ${e.message}` };
      }
    },
    schemaError() {
      return this.parsedSchema.error;
    },
    fields() {
      const s = this.parsedSchema.value;
      return s && s.properties ? Object.keys(s.properties) : [];
    },
    flatFields() {
      const out = [];
      const walk = (schema, depth, prefix) => {
        const req = new Set(schema.required || []);
        Object.entries(schema.properties || {}).forEach(([name, spec]) => {
          const s = spec || {};
          const type = s.type === 'array' ? `array of ${(s.items || {}).type || 'value'}` : `${s.type || 'value'}${s.enum ? ' · enum' : ''}`;
          const range = s.minimum !== undefined || s.maximum !== undefined ? `${s.minimum ?? ''}–${s.maximum ?? ''}` : '';
          out.push({ path: `${prefix}${name}`, name: depth ? `└ ${name}` : name, depth, type, required: req.has(name), enum: s.enum || [], range, description: s.description || '' });
          if (s.type === 'object') walk(s, depth + 1, `${prefix}${name}.`);
          if (s.type === 'array' && (s.items || {}).type === 'object') walk(s.items, depth + 1, `${prefix}${name}[].`);
        });
      };
      if (this.parsedSchema.value) walk(this.parsedSchema.value, 0, '');
      return out;
    },
    payload() {
      return { ...this.form, result_schema: this.parsedSchema.value === undefined ? this.form.result_schema : this.parsedSchema.value };
    },
    changedSections() {
      if (this.isNew) return [];
      const labels = { name: 'name', description: 'description', is_active: 'active', unit: 'unit', population: 'population', inputs: 'inputs', prompt: 'prompt', result_schema: 'result schema', effects: 'effects' };
      return Object.keys(labels).filter((k) => JSON.stringify(this.payload[k] ?? null) !== JSON.stringify(this.saved[k] ?? null)).map((k) => labels[k]);
    },
    diffSections() {
      if (!this.diff) return [];
      const a = { name: this.diff.a.name, description: this.diff.a.description, ...this.diff.a.definition };
      const b = { name: this.diff.b.name, description: this.diff.b.description, ...this.diff.b.definition };
      return ['name', 'description', ...DEFINITION].filter((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k])).map((k) => ({
        key: k, lines: lineDiff(typeof a[k] === 'string' ? a[k] : JSON.stringify(a[k], null, 2), typeof b[k] === 'string' ? b[k] : JSON.stringify(b[k], null, 2)),
      }));
    },
  },
  watch: {
    form: { deep: true, handler: 'derive' },
    schemaText: 'derive',
  },
  mounted() {
    this.load();
  },
  methods: {
    absolute, relative, words,
    async load() {
      try {
        const calls = [this.$axios.get(`${API}/templates/catalog/`), this.$axios.get(`${API}/write-kinds/`)];
        if (!this.isNew) {
          const id = this.$route.params.templateId;
          calls.push(this.$axios.get(`${API}/templates/${id}/`), this.$axios.get(`${API}/templates/${id}/versions/`));
        }
        const [catalog, kinds, template, versions] = await Promise.all(calls);
        this.catalog = catalog.data;
        this.kinds = kinds.data.kinds;
        this.areas = kinds.data.areas;
        if (!this.isNew) {
          this.saved = template.data;
          this.versions = versions.data;
        }
        this.reset();
        this.$emit('crumb', this.isNew ? 'New template' : this.saved.name);
      } catch (e) {
        this.loadError = e?.response?.status === 404 ? 'No template with that id on this tenant.' : errorOf(e, 'The template could not be loaded.');
      } finally {
        this.loading = false;
      }
    },
    reset() {
      const src = this.isNew ? BLANK : this.saved;
      this.form = JSON.parse(JSON.stringify(Object.fromEntries(Object.keys(BLANK).map((k) => [k, src[k] ?? BLANK[k]]))));
      this.schemaText = this.form.result_schema ? JSON.stringify(this.form.result_schema, null, 2) : '';
      this.params = this.saved.params || [];
      this.saveError = '';
    },
    derive() {
      clearTimeout(this.derivedTimer);
      this.derivedTimer = setTimeout(async () => {
        try {
          const { data } = await this.$axios.post(`${API}/templates/params/`, this.payload);
          this.params = data.params;
          this.derivedError = data.error || '';
        } catch {
          this.derivedError = '';
        }
      }, 250);
    },
    move(i, dir) {
      const [item] = this.form.inputs.splice(i, 1);
      this.form.inputs.splice(i + dir, 0, item);
    },
    addInput(kind) {
      const base = { kind, label: `${kind}_${this.form.inputs.length + 1}`, description: '' };
      if (kind === 'data_attribute') base.ref = null;
      if (kind === 'patient_graph') Object.assign(base, { label: 'patient_record', nodes: null, required: true });
      if (kind === 'source') base.ref = (this.catalog.sources[0] || {}).name || '';
      this.form.inputs.push(base);
    },
    setRender(item, value) {
      if (value) item.render = value;
      else delete item.render;
    },
    filterRows(item) {
      return Object.entries(item.filters || {}).map(([name, value]) => ({ name, value }));
    },
    writeFilters(item, rows) {
      const next = Object.fromEntries(rows.filter((r) => r.name !== undefined).map((r) => [r.name, r.value]));
      if (Object.keys(next).length) item.filters = next;
      else delete item.filters;
    },
    setFilter(item, j, key, value) {
      const rows = this.filterRows(item);
      rows[j] = { ...rows[j], [key]: value };
      this.writeFilters(item, rows);
    },
    addFilter(item) {
      this.writeFilters(item, [...this.filterRows(item), { name: `filter_${this.filterRows(item).length + 1}`, value: '' }]);
    },
    removeFilter(item, j) {
      const rows = this.filterRows(item);
      rows.splice(j, 1);
      this.writeFilters(item, rows);
    },
    toggleNode(item, key) {
      const current = item.nodes || [...this.catalog.default_graph_nodes];
      item.nodes = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    },
    restore(version) {
      Object.assign(this.form, JSON.parse(JSON.stringify({ name: version.name, description: version.description, ...version.definition })));
      this.schemaText = version.definition.result_schema ? JSON.stringify(version.definition.result_schema, null, 2) : '';
      this.diff = null;
    },
    async save() {
      if (this.schemaError) {
        this.saveError = this.schemaError;
        return;
      }
      this.saving = true;
      this.saveError = '';
      try {
        if (this.isNew) {
          const { data } = await this.$axios.post(`${API}/templates/`, this.payload);
          this.form = JSON.parse(JSON.stringify(BLANK));
          this.$router.replace({ name: 'AdminHermesTemplate', params: { templateId: data.id } });
          return;
        }
        const { data } = await this.$axios.patch(`${API}/templates/${this.saved.id}/`, this.payload);
        this.saved = data;
        this.versions = (await this.$axios.get(`${API}/templates/${data.id}/versions/`)).data;
        this.reset();
        this.savedNote = `Saved as version ${data.version}.`;
        setTimeout(() => { this.savedNote = ''; }, 4000);
      } catch (e) {
        this.saveError = errorOf(e, 'The platform did not accept the template.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
